import React, { useState } from "react";
import { set } from "firebase/database";

import {
  Stack,
  Button,
  Checkbox,
  Typography,
  FormControlLabel,
} from "@mui/material";
import { useTranslation, Trans } from "gatsby-plugin-react-i18next";

import {
  PRIVACY_POLICY_LINK,
  TERMS_AND_CONDITIONS_LINK,
} from "../../../../consts/links";

import TypographyLink from "../../../../components/TypographyLink";

import TextInput from "./TextInput";
import PaswordValidator from "./PaswordValidator";

export default function SignUpForm({ firebase, user, uid, onSignUpSuccess = () => { }, onSignupFailure = () => { } }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    emailConfirmation: "",
    country: "",
    zipCode: "",
    phone: "",
    password: "",
    acceptTerms: false,
  });

  const handleChange = (event) => {
    try {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [event.target.name]: event.target.value,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const passwordHasSpecial = (pass) => {
    return /\W/.test(pass);
  }

  const passwordHasLength = (pass) => {
    return pass.length >= 8;
  }

  const passwordHasUpper = (pass) => {
    return /[A-Z]/.test(pass);
  }

  const validPassword = (pass) => {
    const result = passwordHasSpecial(pass) && passwordHasLength(pass) && passwordHasUpper(pass);
    console.log("validpassresult", result);
    return result;
  }

  const validPhone = (phone) => {
    const result = /^[+]{0,1}[0-9]{6,16}$/.test(phone);
    console.log('validphone', result);
    return result;
  }

  const formValid = formData.name.length > 0 &&
    formData.lastName.length > 0 &&
    formData.email.length > 0 &&
    formData.email === formData.emailConfirmation &&
    formData.country.length > 0 &&
    ("" + formData.zipCode).length > 0 &&
    validPhone(formData.phone) &&
    validPassword(formData.password) &&
    formData.acceptTerms;

  const handleSubmit = (event) => {
    event.preventDefault();
    console.log('registering');
    firebase
      .doCreateUserWithEmailAndPassword(formData.email, formData.password)
      .then((authUser) => {
        console.log('auth', authUser);
        return set(firebase.user(authUser.user.uid), {
          ...formData,
        });
      }).then(() => {
        onSignUpSuccess();
      }).catch((err) => {
        onSignupFailure(err)
      });
  };

  return (
    <Stack gap={3} component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" color="secondary.light" fontWeight={700}>
        {t("signup")}
      </Typography>
      <Stack direction="column" gap="24px">
        <TextInput
          type="text"
          name="name"
          label={t("registry_inputs.name_label")}
          value={formData.name}
          placeholder={t("registry_inputs.name_placeholder")}
          handleChange={handleChange}
          autoComplete="given-name"
        />
        <TextInput
          type="text"
          name="lastName"
          label={t("registry_inputs.last_name_label")}
          value={formData.lastName}
          placeholder={t("registry_inputs.last_name_placeholder")}
          handleChange={handleChange}
          autoComplete="family-name"
        />
        <TextInput
          type="email"
          name="email"
          label={t("registry_inputs.email_label")}
          value={formData.email}
          placeholder={t("registry_inputs.email_placeholder")}
          handleChange={handleChange}
          autoComplete="email"
        />
        <TextInput
          type="email"
          name="emailConfirmation"
          label={t("registry_inputs.email_confirmation_label")}
          value={formData.emailConfirmation}
          placeholder={t("registry_inputs.email_confirmation_placeholder")}
          handleChange={handleChange}
          disableAutocomplete
          onPaste={(e) => { e.preventDefault(); return false; }}
          onPasteCapture={(e) => { e.preventDefault(); return false; }}
          autoComplete="off"
        />
        <TextInput
          type="text"
          name="country"
          label={t("registry_inputs.country_label")}
          value={formData.country}
          placeholder={t("registry_inputs.country_placeholder")}
          handleChange={handleChange}
          autoComplete="country"
        />

        <TextInput
          type="text"
          name="zipCode"
          label={t("registry_inputs.zipCode_label")}
          value={formData.zipCode}
          placeholder={t("registry_inputs.zipCode_placeholder")}
          handleChange={handleChange}
          autoComplete="postal-code"
        />
        <Stack direction="column" gap="8px">
          <Typography variant="body2" color="secondary.main" fontWeight={500}>
            {t("registry_inputs.phone_label")}
          </Typography>
          <TextInput
            type="tel"
            name="phone"
            value={formData.phone}
            placeholder={t("registry_inputs.phone_placeholder")}
            handleChange={handleChange}
            autoComplete="tel"
          />
        </Stack>
        <PaswordValidator
          value={formData.password}
          handleChange={handleChange}
        />
        <FormControlLabel
          control={
            <Checkbox
              required
              variant="grey"
              onChange={(event) =>
                setFormData((prevFormData) => ({
                  ...prevFormData,
                  acceptTerms: event.target.checked,
                }))
              }
            />
          }
          label={
            <Typography color="secondary.light" variant="body2">
              <Trans
                i18nKey="accept_terms_conditions_privacy_policy_by_registering"
                components={[
                  <TypographyLink href={TERMS_AND_CONDITIONS_LINK} bold />,
                  <TypographyLink href={PRIVACY_POLICY_LINK} bold />,
                ]}
              />
            </Typography>
          }
        />
        <Button disabled={!formValid} type="submit" color="primary" variant="contained" fullWidth>
          {t("register_me")}
        </Button>
        <Typography color="secondary.main" variant="body2" textAlign="center">
          <Trans
            i18nKey="already_have_account_sign_in"
            components={[<TypographyLink href="/login" color="primary.light" bold />]}
          />
        </Typography>
      </Stack>
    </Stack>
  );
}
